import { useEffect, useState } from "react";
import api from "@/lib/api";
import { errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash2, Calendar, CalendarPlus, Filter, X, Play, Pause, RotateCcw, MessageCircle } from "lucide-react";
import { fmtDate } from "@/lib/format";
import { toast } from "sonner";
import AttachmentsField from "@/components/AttachmentsField";
import { openWa, waNumberWarning } from "@/lib/whatsapp";

const empty = { title: "", description: "", due_date: "", priority: "media", project_id: "", client_id: "", hours: 0, attachments: [] };

const prioClass = { alta: "bg-zen-alert/15 text-zen-alert", media: "bg-zen-info/20 text-zen-ink", bassa: "bg-zen-subtle/40 text-zen-ink2" };

/**
 * Build a Google Calendar "Add event" URL.
 * If hours > 0 → timed event starting at 09:00 on due_date, duration=hours (min 30min).
 * Otherwise → all-day event on due_date.
 */
function buildGoogleCalendarUrl(task, projectName, clientName) {
  const title = encodeURIComponent(task.title || "Attività FreelanZen");
  const context = [
    task.description && task.description,
    projectName && `Progetto: ${projectName}`,
    clientName && `Cliente: ${clientName}`,
    task.priority && `Priorità: ${task.priority}`,
    "\n— Creato da FreelanZen",
  ].filter(Boolean).join("\n");
  const details = encodeURIComponent(context);

  let dates;
  if (!task.due_date) {
    // No date → today all-day
    const today = new Date();
    const y = today.getUTCFullYear();
    const m = String(today.getUTCMonth() + 1).padStart(2, "0");
    const d = String(today.getUTCDate()).padStart(2, "0");
    const dayStr = `${y}${m}${d}`;
    dates = `${dayStr}/${dayStr}`;
  } else if (task.hours && task.hours > 0) {
    // Timed event: 09:00 local for `hours` (min 30min)
    const startLocal = new Date(task.due_date + "T09:00:00");
    const durationMs = Math.max(30, task.hours * 60) * 60 * 1000;
    const endLocal = new Date(startLocal.getTime() + durationMs);
    const fmt = (dt) => {
      const y = dt.getUTCFullYear();
      const M = String(dt.getUTCMonth() + 1).padStart(2, "0");
      const D = String(dt.getUTCDate()).padStart(2, "0");
      const h = String(dt.getUTCHours()).padStart(2, "0");
      const m = String(dt.getUTCMinutes()).padStart(2, "0");
      return `${y}${M}${D}T${h}${m}00Z`;
    };
    dates = `${fmt(startLocal)}/${fmt(endLocal)}`;
  } else {
    // All-day event on due_date (Google needs YYYYMMDD/YYYYMMDD_next_day)
    const start = new Date(task.due_date + "T00:00:00Z");
    const end = new Date(start.getTime() + 24 * 60 * 60 * 1000);
    const dayStr = (dt) => {
      const y = dt.getUTCFullYear();
      const m = String(dt.getUTCMonth() + 1).padStart(2, "0");
      const d = String(dt.getUTCDate()).padStart(2, "0");
      return `${y}${m}${d}`;
    };
    dates = `${dayStr(start)}/${dayStr(end)}`;
  }
  return `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${title}&dates=${dates}&details=${details}`;
}

export default function Tasks() {
  const [tasks, setTasks] = useState([]);
  const [projects, setProjects] = useState([]);
  const [clients, setClients] = useState([]);
  const [statusFilter, setStatusFilter] = useState("attive"); // attive | completate | tutte
  const [viewFilter, setViewFilter] = useState("tutte");       // tutte | oggi | urgenti | scadenza
  const [projectFilter, setProjectFilter] = useState("_all");  // _all | project_id | _none
  const [tick, setTick] = useState(0);

  // New-task dialog
  const [newOpen, setNewOpen] = useState(false);
  const [newForm, setNewForm] = useState(empty);

  // Edit dialog
  const [editOpen, setEditOpen] = useState(false);
  const [editTask, setEditTask] = useState(null);

  const load = async () => {
    const [t, p, c] = await Promise.all([api.get("/tasks"), api.get("/projects"), api.get("/clients")]);
    setTasks(t.data); setProjects(p.data); setClients(c.data);
  };
  useEffect(() => { load(); }, []);

  // Global 1-sec tick so running timers refresh live
  useEffect(() => {
    const anyRunning = tasks.some(t => t.timer_started_at);
    if (!anyRunning) return;
    const id = setInterval(() => setTick(x => x + 1), 1000);
    return () => clearInterval(id);
  }, [tasks]);

  const create = async () => {
    if (!newForm.title.trim()) return toast.error("Inserisci un titolo");
    try {
      const payload = { ...newForm,
        project_id: newForm.project_id || null,
        client_id: newForm.client_id || null,
      };
      await api.post("/tasks", payload);
      toast.success("Attività creata");
      setNewOpen(false); setNewForm(empty); load();
    } catch (e) { toast.error(errMsg(e, "Errore")); }
  };

  const saveEdit = async () => {
    if (!editTask) return;
    if (!editTask.title.trim()) return toast.error("Il titolo è obbligatorio");
    try {
      const payload = {
        title: editTask.title,
        description: editTask.description,
        due_date: editTask.due_date || null,
        priority: editTask.priority,
        hours: parseFloat(editTask.hours) || 0,
        project_id: editTask.project_id || null,
        client_id: editTask.client_id || null,
        completed: !!editTask.completed,
        attachments: editTask.attachments || [],
      };
      await api.put(`/tasks/${editTask.task_id}`, payload);
      toast.success("Attività aggiornata");
      setEditOpen(false); setEditTask(null); load();
    } catch (e) { toast.error(errMsg(e, "Errore")); }
  };

  const toggle = async (t) => {
    await api.put(`/tasks/${t.task_id}`, { completed: !t.completed });
    load();
  };

  const startTimer = async (t, ev) => {
    ev?.stopPropagation?.();
    try {
      await api.post(`/tasks/${t.task_id}/timer/start`);
      load();
    } catch (e) { toast.error(errMsg(e, "Errore timer")); }
  };
  const stopTimer = async (t, ev) => {
    ev?.stopPropagation?.();
    try {
      await api.post(`/tasks/${t.task_id}/timer/stop`);
      load();
      toast.success("Tempo registrato");
    } catch (e) { toast.error(errMsg(e, "Errore timer")); }
  };
  const resetTimer = async (t, ev) => {
    ev?.stopPropagation?.();
    if (!window.confirm(`Azzerare il tempo registrato per "${t.title}"?`)) return;
    await api.post(`/tasks/${t.task_id}/timer/reset`);
    load();
  };

  const fmtDuration = (secs) => {
    const s = Math.max(0, Math.floor(secs));
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    const ss = s % 60;
    if (h > 0) return `${h}h ${String(m).padStart(2,"0")}m`;
    return `${m}:${String(ss).padStart(2,"0")}`;
  };
  const liveSeconds = (t) => {
    let s = t.time_seconds || 0;
    if (t.timer_started_at) {
      const started = new Date(t.timer_started_at).getTime();
      const now = Date.now();
      s += Math.max(0, Math.floor((now - started) / 1000));
    }
    return s;
  };

  const remove = async (id, e) => {
    e?.stopPropagation?.();
    if (!window.confirm("Eliminare questa attività?")) return;
    await api.delete(`/tasks/${id}`);
    load();
  };

  const projectName = (id) => projects.find(p=>p.project_id===id)?.name;
  const clientName = (id) => clients.find(c=>c.client_id===id)?.name;

  const openEdit = (t) => {
    setEditTask({ ...t, due_date: t.due_date ? t.due_date.slice(0,10) : "" });
    setEditOpen(true);
  };

  const addToGoogleCalendar = () => {
    if (!editTask) return;
    const url = buildGoogleCalendarUrl(
      editTask,
      projectName(editTask.project_id),
      clientName(editTask.client_id)
    );
    window.open(url, "_blank", "noopener,noreferrer");
    toast.success("Apro Google Calendar…");
  };

  const prioRank = { alta: 0, media: 1, bassa: 2 };
  const today = new Date().toISOString().slice(0, 10);

  let filtered = tasks.slice();

  // Status filter
  if (statusFilter === "attive") filtered = filtered.filter(t => !t.completed);
  else if (statusFilter === "completate") filtered = filtered.filter(t => t.completed);

  // Project filter
  if (projectFilter === "_none") filtered = filtered.filter(t => !t.project_id);
  else if (projectFilter !== "_all") filtered = filtered.filter(t => t.project_id === projectFilter);

  // View filter (Oggi / Urgenti / Scadenza / Tutte)
  if (viewFilter === "oggi") {
    filtered = filtered.filter(t => t.due_date && t.due_date.slice(0, 10) === today);
    filtered.sort((a, b) => prioRank[a.priority] - prioRank[b.priority]);
  } else if (viewFilter === "urgenti") {
    filtered.sort((a, b) => {
      const p = prioRank[a.priority] - prioRank[b.priority];
      if (p !== 0) return p;
      return (a.due_date || "9999").localeCompare(b.due_date || "9999");
    });
  } else if (viewFilter === "scadenza") {
    filtered = filtered.filter(t => t.due_date);
    filtered.sort((a, b) => a.due_date.localeCompare(b.due_date));
  } else {
    filtered.sort((a, b) => (a.due_date || "9999").localeCompare(b.due_date || "9999"));
  }

  const activeFilterCount =
    (statusFilter !== "attive" ? 1 : 0) +
    (viewFilter !== "tutte" ? 1 : 0) +
    (projectFilter !== "_all" ? 1 : 0);

  const resetFilters = () => {
    setStatusFilter("attive"); setViewFilter("tutte"); setProjectFilter("_all");
  };

  const todayCount = tasks.filter(t => !t.completed && t.due_date && t.due_date.slice(0, 10) === today).length;
  const urgentCount = tasks.filter(t => !t.completed && t.priority === "alta").length;

  return (
    <div className="space-y-6" data-testid="tasks-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <span className="zen-label">Agenda</span>
          <h1 className="mt-2 font-heading text-3xl">Attività</h1>
          <p className="text-zen-ink2 mt-1">{tasks.filter(t=>!t.completed).length} da fare · {tasks.filter(t=>t.completed).length} completate</p>
        </div>
        <div className="flex gap-3">
          <Dialog open={newOpen} onOpenChange={setNewOpen}>
            <DialogTrigger asChild>
              <Button data-testid="add-task-btn" className="bg-zen-primary hover:bg-zen-primaryHover rounded-full"><Plus size={16} className="mr-1"/>Nuova attività</Button>
            </DialogTrigger>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle>Nuova attività</DialogTitle>
                <DialogDescription className="sr-only">Inserisci titolo, scadenza, priorità, progetto e allegati.</DialogDescription>
              </DialogHeader>
              <div className="grid gap-4">
                <div><Label>Titolo *</Label><Input data-testid="tf-title" value={newForm.title} onChange={(e)=>setNewForm({...newForm, title:e.target.value})}/></div>
                <div><Label>Descrizione</Label><Textarea rows={2} value={newForm.description} onChange={(e)=>setNewForm({...newForm, description:e.target.value})}/></div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div><Label>Scadenza</Label><Input type="date" data-testid="tf-due" value={newForm.due_date} onChange={(e)=>setNewForm({...newForm, due_date:e.target.value})}/></div>
                  <div><Label>Priorità</Label>
                    <Select value={newForm.priority} onValueChange={(v)=>setNewForm({...newForm, priority:v})}>
                      <SelectTrigger><SelectValue/></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="bassa">Bassa</SelectItem>
                        <SelectItem value="media">Media</SelectItem>
                        <SelectItem value="alta">Alta</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div><Label>Progetto</Label>
                  <Select value={newForm.project_id||"_none"} onValueChange={(v)=>setNewForm({...newForm, project_id: v==="_none"?"":v})}>
                    <SelectTrigger><SelectValue placeholder="Nessuno"/></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="_none">Nessuno</SelectItem>
                      {projects.map(p=><SelectItem key={p.project_id} value={p.project_id}>{p.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div><Label>Ore stimate</Label><Input type="number" step="0.25" value={newForm.hours} onChange={(e)=>setNewForm({...newForm, hours:parseFloat(e.target.value)||0})}/></div>
                <div className="min-w-0">
                  <Label>Allegati Google Drive</Label>
                  <div className="mt-1.5">
                    <AttachmentsField
                      testId="tf-attachments"
                      value={newForm.attachments || []}
                      onChange={(next)=>setNewForm({...newForm, attachments: next})}
                    />
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button variant="ghost" onClick={()=>setNewOpen(false)}>Annulla</Button>
                <Button data-testid="tf-save" onClick={create} className="bg-zen-primary hover:bg-zen-primaryHover">Crea</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Filter bar */}
      <div className="zen-card p-4" data-testid="filter-bar">
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2 text-zen-ink2 mr-1">
            <Filter size={15}/>
            <span className="text-sm font-medium">Vista</span>
          </div>
          {[
            { v: "tutte", label: "Tutte" },
            { v: "oggi", label: `Oggi${todayCount ? ` · ${todayCount}` : ""}` },
            { v: "urgenti", label: `Urgenti${urgentCount ? ` · ${urgentCount}` : ""}` },
            { v: "scadenza", label: "Per scadenza" },
          ].map(x => (
            <button
              key={x.v}
              data-testid={`view-${x.v}`}
              onClick={() => setViewFilter(x.v)}
              className={`px-3.5 py-1.5 rounded-full text-sm transition-colors ${viewFilter === x.v ? "bg-zen-primary text-white" : "bg-white border border-zen-border text-zen-ink2 hover:bg-zen-subtle/20"}`}
            >
              {x.label}
            </button>
          ))}
          <div className="w-px h-6 bg-zen-border mx-1 hidden sm:block"/>
          <div className="flex items-center gap-2">
            <span className="text-sm text-zen-ink2 hidden sm:inline">Progetto</span>
            <Select value={projectFilter} onValueChange={setProjectFilter}>
              <SelectTrigger data-testid="project-filter" className="w-56"><SelectValue/></SelectTrigger>
              <SelectContent>
                <SelectItem value="_all">Tutti i progetti</SelectItem>
                <SelectItem value="_none">Senza progetto</SelectItem>
                {projects.map(p => <SelectItem key={p.project_id} value={p.project_id}>{p.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-2 ml-auto">
            <span className="text-sm text-zen-ink2 hidden sm:inline">Stato</span>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger data-testid="status-filter" className="w-40"><SelectValue/></SelectTrigger>
              <SelectContent>
                <SelectItem value="attive">Attive</SelectItem>
                <SelectItem value="completate">Completate</SelectItem>
                <SelectItem value="tutte">Tutte</SelectItem>
              </SelectContent>
            </Select>
            {activeFilterCount > 0 && (
              <button
                onClick={resetFilters}
                data-testid="reset-filters"
                className="text-sm text-zen-alert hover:underline flex items-center gap-1"
                title="Reset filtri"
              >
                <X size={14}/> Reset
              </button>
            )}
          </div>
        </div>
        <div className="mt-2 text-xs text-zen-ink2">
          Mostrando {filtered.length} {filtered.length === 1 ? "attività" : "attività"}
          {viewFilter !== "tutte" && ` · vista "${viewFilter === "oggi" ? "Oggi" : viewFilter === "urgenti" ? "Urgenti" : "Per scadenza"}"`}
          {projectFilter !== "_all" && ` · ${projectFilter === "_none" ? "senza progetto" : projects.find(p=>p.project_id===projectFilter)?.name || ""}`}
        </div>
      </div>

      {filtered.length === 0 ? (
        <div className="zen-card p-12 text-center text-zen-ink2">Nessuna attività in questa vista.</div>
      ) : (
        <div className="zen-card divide-y divide-zen-border">
          {filtered.map((t) => (
            <div
              key={t.task_id}
              data-testid={`task-${t.task_id}`}
              className="p-4 flex flex-col sm:flex-row sm:items-start gap-3 sm:gap-4 group hover:bg-zen-bg/50 transition-colors cursor-pointer"
              onClick={()=>openEdit(t)}
            >
              <div className="flex items-start gap-3 flex-1 min-w-0 w-full">
                <div onClick={(e)=>e.stopPropagation()} className="mt-1 shrink-0">
                  <Checkbox
                    data-testid={`toggle-task-${t.task_id}`}
                    checked={t.completed} onCheckedChange={()=>toggle(t)}
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <div className={`font-medium break-words ${t.completed ? "line-through text-zen-ink2" : "text-zen-ink"}`}>{t.title}</div>
                  {t.description && <div className="text-sm text-zen-ink2 mt-0.5 line-clamp-1">{t.description}</div>}
                  <div className="flex items-center gap-x-3 gap-y-1 mt-2 text-xs text-zen-ink2 flex-wrap">
                    {t.due_date && <span className="flex items-center gap-1"><Calendar size={12}/> {fmtDate(t.due_date)}</span>}
                    {t.project_id && projectName(t.project_id) && <span>· {projectName(t.project_id)}</span>}
                    {t.hours > 0 && <span>· stima {t.hours}h</span>}
                    {(t.time_seconds > 0 || t.timer_started_at) && (
                      <span
                        data-testid={`task-timer-${t.task_id}`}
                        className={`inline-flex items-center gap-1 font-mono ${t.timer_started_at ? "text-zen-primary font-semibold" : "text-zen-ink2"}`}
                      >
                        {t.timer_started_at && <span className="inline-block w-1.5 h-1.5 bg-zen-primary rounded-full animate-pulse"/>}
                        {fmtDuration(liveSeconds(t))}
                      </span>
                    )}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-1.5 self-end sm:self-start shrink-0" onClick={(e)=>e.stopPropagation()}>
                {(() => {
                  const cli = clients.find(c => c.client_id === t.client_id);
                  const wa = cli?.whatsapp || cli?.phone;
                  if (!wa) return null;
                  return (
                    <button
                      data-testid={`task-wa-${t.task_id}`}
                      onClick={(e)=>{
                        e.stopPropagation();
                        const ok = openWa(wa, `Ciao ${cli.name?.split(" ")[0] || ""}, ti scrivo per l'attività: ${t.title}`);
                        if (!ok) toast.error(waNumberWarning(wa));
                      }}
                      title={`Scrivi su WhatsApp a ${cli.company || cli.name}`}
                      className="w-7 h-7 rounded-full bg-zen-primary/10 text-zen-primary hover:bg-zen-primary/20 border border-zen-primary/20 flex items-center justify-center transition-colors"
                    >
                      <MessageCircle size={13}/>
                    </button>
                  );
                })()}
                {t.timer_started_at ? (
                  <button
                    data-testid={`stop-timer-${t.task_id}`}
                    onClick={(e)=>stopTimer(t, e)}
                    title="Ferma cronometro"
                    className="w-8 h-8 rounded-full bg-zen-alert/15 text-zen-alert hover:bg-zen-alert/25 flex items-center justify-center transition-colors"
                  ><Pause size={14}/></button>
                ) : (
                  <button
                    data-testid={`start-timer-${t.task_id}`}
                    onClick={(e)=>startTimer(t, e)}
                    disabled={t.completed}
                    title="Avvia cronometro"
                    className="w-8 h-8 rounded-full bg-zen-primary/10 text-zen-primary hover:bg-zen-primary/20 flex items-center justify-center transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                  ><Play size={14} className="ml-0.5"/></button>
                )}
                {t.time_seconds > 0 && !t.timer_started_at && (
                  <button
                    data-testid={`reset-timer-${t.task_id}`}
                    onClick={(e)=>resetTimer(t, e)}
                    title="Azzera tempo"
                    className="w-8 h-8 rounded-full text-zen-ink2 hover:bg-zen-subtle/20 flex items-center justify-center transition-colors"
                  ><RotateCcw size={13}/></button>
                )}
                <Badge className={`${prioClass[t.priority]} font-normal rounded-full`}>{t.priority}</Badge>
                <button data-testid={`del-task-${t.task_id}`} onClick={(e)=>remove(t.task_id, e)} className="text-zen-alert opacity-0 group-hover:opacity-100 transition-opacity ml-1"><Trash2 size={15}/></button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Edit dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto" data-testid="edit-task-dialog">
          <DialogHeader>
            <DialogTitle>Modifica attività</DialogTitle>
            <DialogDescription className="sr-only">Modifica i campi dell&apos;attività, aggiungi allegati o esportala su Google Calendar.</DialogDescription>
          </DialogHeader>
          {editTask && (
            <div className="grid gap-4">
              <div>
                <Label>Titolo *</Label>
                <Input data-testid="et-title" value={editTask.title} onChange={(e)=>setEditTask({...editTask, title:e.target.value})}/>
              </div>
              <div>
                <Label>Descrizione</Label>
                <Textarea data-testid="et-description" rows={3} value={editTask.description||""} onChange={(e)=>setEditTask({...editTask, description:e.target.value})}/>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Scadenza</Label>
                  <Input type="date" data-testid="et-due" value={editTask.due_date||""} onChange={(e)=>setEditTask({...editTask, due_date:e.target.value})}/>
                </div>
                <div>
                  <Label>Priorità</Label>
                  <Select value={editTask.priority} onValueChange={(v)=>setEditTask({...editTask, priority:v})}>
                    <SelectTrigger data-testid="et-priority"><SelectValue/></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="bassa">Bassa</SelectItem>
                      <SelectItem value="media">Media</SelectItem>
                      <SelectItem value="alta">Alta</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label>Progetto</Label>
                <Select value={editTask.project_id||"_none"} onValueChange={(v)=>setEditTask({...editTask, project_id: v==="_none"?"":v})}>
                  <SelectTrigger data-testid="et-project"><SelectValue placeholder="Nessuno"/></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_none">Nessuno</SelectItem>
                    {projects.map(p=><SelectItem key={p.project_id} value={p.project_id}>{p.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Cliente</Label>
                <Select value={editTask.client_id||"_none"} onValueChange={(v)=>setEditTask({...editTask, client_id: v==="_none"?"":v})}>
                  <SelectTrigger data-testid="et-client"><SelectValue placeholder="Nessuno"/></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_none">Nessuno</SelectItem>
                    {clients.map(c=><SelectItem key={c.client_id} value={c.client_id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Ore stimate</Label>
                <Input type="number" step="0.25" data-testid="et-hours" value={editTask.hours||0} onChange={(e)=>setEditTask({...editTask, hours:parseFloat(e.target.value)||0})}/>
              </div>
              <div className="flex items-center gap-2 pt-1">
                <Checkbox data-testid="et-completed" checked={!!editTask.completed} onCheckedChange={(v)=>setEditTask({...editTask, completed: !!v})}/>
                <Label className="cursor-pointer" onClick={()=>setEditTask({...editTask, completed: !editTask.completed})}>Completata</Label>
              </div>

              <div className="min-w-0">
                <Label>Allegati Google Drive</Label>
                <div className="mt-1.5">
                  <AttachmentsField
                    testId="et-attachments"
                    value={editTask.attachments || []}
                    onChange={(next)=>setEditTask({...editTask, attachments: next})}
                  />
                </div>
              </div>

              <div className="pt-2 border-t border-zen-border">
                <Button
                  type="button"
                  variant="outline"
                  onClick={addToGoogleCalendar}
                  data-testid="gcal-add-btn"
                  className="w-full rounded-full border-zen-primary/30 text-zen-primary hover:bg-zen-primary/5"
                >
                  <CalendarPlus size={16} className="mr-2"/> Aggiungi promemoria a Google Calendar
                </Button>
                <p className="text-xs text-zen-ink2 mt-2 text-center">
                  Apre Google Calendar precompilato — conferma con un click nel tuo account.
                </p>
              </div>
            </div>
          )}
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button
              variant="ghost"
              onClick={()=>{ if (window.confirm("Eliminare questa attività?")) { api.delete(`/tasks/${editTask.task_id}`).then(()=>{setEditOpen(false); load();}); } }}
              data-testid="et-delete"
              className="text-zen-alert hover:text-zen-alert/80 hover:bg-zen-alert/5 sm:mr-auto"
            >
              <Trash2 size={14} className="mr-1"/> Elimina
            </Button>
            <div className="flex gap-2">
              <Button variant="ghost" onClick={()=>setEditOpen(false)}>Annulla</Button>
              <Button data-testid="et-save" onClick={saveEdit} className="bg-zen-primary hover:bg-zen-primaryHover">Salva</Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
